import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { getAvailabilitySettings, updateAvailabilitySettings } from '../services/calendarService';
import {
  CALENDAR_AVAILABILITY_SETTING_KEYS,
  CHATBOT_EMPLOYEE_SELECTION_MODES,
  type CalendarAvailabilitySettingKey,
  type CalendarAvailabilitySettings,
  type ChatBotEmployeeSelection,
} from '../types/Calendar';
import styles from './CalendarAvailabilitySettingsModal.module.css';

type CalendarAvailabilitySettingsModalProps = {
  isOpen: boolean;
  canManage: boolean;
  onClose: () => void;
  onSaved?: (settings: CalendarAvailabilitySettings) => void;
};

/** Turning this consideration off lets the same time be booked twice. */
const RISKY_KEYS: readonly CalendarAvailabilitySettingKey[] = ['respectExistingBookings'];

export const CalendarAvailabilitySettingsModal: React.FC<CalendarAvailabilitySettingsModalProps> = ({
  isOpen,
  canManage,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { showToast } = useToast();

  const [savedSettings, setSavedSettings] = React.useState<CalendarAvailabilitySettings | null>(null);
  const [draft, setDraft] = React.useState<CalendarAvailabilitySettings | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const result = await getAvailabilitySettings();
        if (cancelled) return;
        setSavedSettings(result);
        setDraft(result);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : t('availabilitySettings.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const changedKeys = React.useMemo(() => {
    if (!draft || !savedSettings) return [];
    return CALENDAR_AVAILABILITY_SETTING_KEYS.filter((key) => draft[key] !== savedSettings[key]);
  }, [draft, savedSettings]);

  const employeeSelectionChanged = Boolean(
    draft && savedSettings && draft.chatBotEmployeeSelection !== savedSettings.chatBotEmployeeSelection,
  );
  const hasChanges = changedKeys.length > 0 || employeeSelectionChanged;

  const onToggle = (key: CalendarAvailabilitySettingKey) => {
    setDraft((current) => (current ? { ...current, [key]: !current[key] } : current));
  };

  const onReset = () => {
    setDraft(savedSettings);
  };

  const onSave = async () => {
    if (!draft) return;

    if (!hasChanges) {
      showToast(t('availabilitySettings.noChanges'), 'success');
      return;
    }

    const changes = changedKeys.reduce<Partial<CalendarAvailabilitySettings>>((accumulator, key) => {
      accumulator[key] = draft[key];
      return accumulator;
    }, {});
    if (employeeSelectionChanged) {
      changes.chatBotEmployeeSelection = draft.chatBotEmployeeSelection;
    }

    setSaving(true);
    try {
      const result = await updateAvailabilitySettings(changes);
      setSavedSettings(result);
      setDraft(result);
      showToast(t('availabilitySettings.saved'), 'success');
      onSaved?.(result);
    } catch (saveError) {
      showToast(
        saveError instanceof Error ? saveError.message : t('availabilitySettings.saveFailed'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const disabledCount = draft
    ? CALENDAR_AVAILABILITY_SETTING_KEYS.filter((key) => !draft[key]).length
    : 0;

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-availability-settings-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 id="calendar-availability-settings-title" className={styles.title}>
            {t('availabilitySettings.title')}
          </h3>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('close', { ns: 'common' })}
          </button>
        </div>

        <p className={styles.intro}>{t('availabilitySettings.intro')}</p>

        {!canManage ? (
          <p className={styles.notice}>{t('availabilitySettings.readOnly')}</p>
        ) : null}

        {loading ? (
          <div className={styles.loadingState}>
            <Spinner text={t('availabilitySettings.loading')} />
          </div>
        ) : null}

        {!loading && error ? <p className={styles.errorState}>{error}</p> : null}

        {!loading && !error && draft ? (
          <div className={styles.settingsBody}>
            <div className={styles.employeeSelectionCard}>
              <label className={styles.considerationName} htmlFor="chatbot-employee-selection">
                {t('availabilitySettings.employeeSelection.label')}
              </label>
              <p className={styles.considerationHelp}>{t('availabilitySettings.employeeSelection.help')}</p>
              <select
                id="chatbot-employee-selection"
                className={styles.employeeSelectionSelect}
                value={draft.chatBotEmployeeSelection}
                disabled={!canManage || saving}
                onChange={(event) =>
                  setDraft((current) =>
                    current
                      ? { ...current, chatBotEmployeeSelection: event.target.value as ChatBotEmployeeSelection }
                      : current,
                  )
                }
              >
                {CHATBOT_EMPLOYEE_SELECTION_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`availabilitySettings.employeeSelection.modes.${mode}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.considerationGrid}>
            {CALENDAR_AVAILABILITY_SETTING_KEYS.map((key) => {
              const checkboxId = `availability-setting-${key}`;
              const isApplied = draft[key];
              const showRiskWarning = !isApplied && RISKY_KEYS.includes(key);

              return (
                <div key={key} className={styles.considerationCard}>
                  <div className={styles.considerationHead}>
                    <label className={styles.considerationName} htmlFor={checkboxId}>
                      {t(`availabilitySettings.considerations.${key}.label`)}
                    </label>
                    <span
                      className={`${styles.statusBadge} ${isApplied ? styles.statusApplied : styles.statusIgnored}`}
                    >
                      {isApplied
                        ? t('availabilitySettings.applied')
                        : t('availabilitySettings.ignored')}
                    </span>
                  </div>

                  <p className={styles.considerationHelp}>
                    {t(`availabilitySettings.considerations.${key}.help`)}
                  </p>

                  <label className={styles.toggleRow}>
                    <input
                      id={checkboxId}
                      type="checkbox"
                      checked={isApplied}
                      disabled={!canManage || saving}
                      onChange={() => onToggle(key)}
                    />
                    <span>{t('availabilitySettings.applyThisCheck')}</span>
                  </label>

                  {showRiskWarning ? (
                    <p className={styles.riskWarning}>
                      {t(`availabilitySettings.considerations.${key}.warning`)}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
          </div>
        ) : null}

        <div className={styles.footer}>
          <span className={styles.summary}>
            {disabledCount === 0
              ? t('availabilitySettings.allApplied')
              : t('availabilitySettings.ignoredCount', { count: disabledCount })}
          </span>
          <div className={styles.footerActions}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onReset}
              disabled={saving || !hasChanges}
            >
              {t('availabilitySettings.discardChanges')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={!canManage || loading || saving || !hasChanges}
            >
              {saving ? t('availabilitySettings.saving') : t('availabilitySettings.save')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CalendarAvailabilitySettingsModal;
